"use client";

import { useEffect } from "react";
import { Button } from "@heroui/react";
import Link from "next/link";
import { RefreshCw, Home, AlertTriangle } from "lucide-react";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error("Global error caught:", error);
  }, [error]);

  const isServerError =
    error.message?.includes("fetch") ||
    error.message?.includes("network") ||
    error.message?.includes("500") ||
    error.digest;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-surface via-canvas to-surface">
      <div className="max-w-md w-full mx-4 text-center">
        {/* Error Icon */}

        {/* Error Message */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-ink mb-4">
            Something went wrong
          </h1>

          {isServerError ? (
            <div>
              <p className="text-muted mb-2">
                Our servers are currently experiencing issues.
              </p>
              <p className="text-subtle text-sm">
                Please try again in a few moments or return to the homepage.
              </p>
            </div>
          ) : (
            <div>
              <p className="text-muted mb-2">
                An unexpected error occurred while loading this page.
              </p>
              <p className="text-subtle text-sm">
                Please try refreshing the page or return to the homepage.
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            onPress={reset}
            color="primary"
            size="md"
            className="w-full text-primary-foreground"
            startContent={<RefreshCw className="w-4 h-4" />}
          >
            Try Again
          </Button>

          <Link href="/">
            <Button
              variant="bordered"
              size="md"
              className="w-full border-line-strong text-ink-secondary hover:bg-surface-raised"
              startContent={<Home className="w-4 h-4" />}
            >
              Go to Homepage
            </Button>
          </Link>
        </div>

        {/* Additional Help */}
        <div className="mt-8 pt-6 border-t border-line">
          <p className="text-subtle text-xs">
            If this problem persists, please{" "}
            <Link
              href="/about-techno-music-world#contact"
              className="text-accent hover:text-theme-neon-300 underline"
            >
              contact us
            </Link>
          </p>
        </div>

        {/* Error Details (Development Only) */}
        {process.env.NODE_ENV === "development" && (
          <details className="mt-6 text-left">
            <summary className="text-subtle text-sm cursor-pointer hover:text-muted">
              Error Details (Dev Only)
            </summary>
            <div className="mt-2 p-3 bg-surface rounded border border-line">
              <p className="text-status-negative text-xs font-mono break-all">
                {error.message}
              </p>
              {error.digest && (
                <p className="text-subtle text-xs mt-1">
                  Error ID: {error.digest}
                </p>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
